import React, { useEffect, useState, useCallback } from "react";
import { useIonRouter } from "@ionic/react";
import { IonPage, IonToast, IonContent } from "@ionic/react";
import { useStorage } from "../../../contexts/StorageContext";
import {
  getMyFollowers,
  getFollowerRequests,
  acceptFollowRequest,
  rejectFollowRequest,
  removeFollower,
  cancelFollowRequest,
  relationStats,
  pendingRequestsActionRequired,
} from "../../../services/datashare";
import Header from "../../../components/Header";
import ActionButtons from "../../../components/ui/popups/ActionButtons";
import DataSharingModal from "../datasharing/SharingModal";
import TabNavigation from "../datasharing/TabNavigation";
import ShareOrgsSection from "../datasharing/ShareOrgsSection";
import ShareDataChooser, { ShareTarget } from "../datasharing/ShareDataChooser";
import { useOrgSharing, orgDisplayName } from "../datasharing/useOrgSharing";
import { useMyOrgRelationship } from "../datasharing/useMyOrgRelationship";
import { useOrgRemoval } from "../datasharing/useOrgRemoval";
import OrgRemoveConfirm from "../datasharing/OrgRemoveConfirm";
import { leavableOrgs } from "../../../services/orgRelationship";
import { OrgSharingProvider } from "../datasharing/OrgSharingContext";
import FollowersList from "../datasharing/FollowersList";
import { DataSharingFollower } from "../datasharing/DataSharingTypes";
import {
  getIonicStorage,
  getStoredPreferences,
  ionicStorage,
  storePrefrences,
} from "../../../services/storageService";
import { useSocket } from "../../../contexts/SocketContext";

interface DisplayDataSharingFollower extends DataSharingFollower {
  name: string;
  email: string;
  phone: string | null;
  dateAdded: string;
  initials: string;
}

const FollowersPage: React.FC = () => {
  const [followers, setFollowers] = useState<DisplayDataSharingFollower[]>([]);
  const [pendingFollowerRequests, setPendingFollowerRequests] = useState<
    DisplayDataSharingFollower[]
  >([]);
  const [actionRequired, setActionRequired] = useState<
    DisplayDataSharingFollower[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"followers" | "following">(
    "followers"
  );
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [stats, setStats] = useState<any>({
    followers_count: 0,
    following_count: 0,
    pending_incoming: {
      follow_requests: 0,
      total: 0,
    },
    pending_outgoing: {
      follow_requests: 0,
      total: 0,
    },
  });
  // Modal states
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAcceptRejectModal, setShowAcceptRejectModal] = useState(false);
  const [selectedFollower, setSelectedFollower] =
    useState<DisplayDataSharingFollower | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  // Org sharing (ACDPA-186): one hook instance owned here and shared with the discovery section
  // + invite popup via OrgSharingProvider. Connected orgs render inside "Who can see my data".
  const orgSharing = useOrgSharing();
  // ⭐ F-492 (UAT, URS_6.8) — ONE relationship hook for the screen: `ShareOrgsSection` reads it
  // (for the invitation card and, via `leavableOrgs`, the association) and the X on an
  // organization row LEAVES through it (`useOrgRemoval`), so everything updates the moment the
  // patient leaves. The X is the patient's only way to leave. (F-523 removed the association's
  // own "Your equipment provider" card display; the hook and the leave path are unchanged.)
  const orgRelationship = useMyOrgRelationship();
  const orgRemoval = useOrgRemoval(orgRelationship, orgSharing);
  // F-506: the ONE "Share my data" button opens this chooser; the choice picks the share popup's
  // flow: "people" shows the person-invite form, "org" the org-focused search + list.
  const [showShareChooser, setShowShareChooser] = useState(false);
  const [inviteFocus, setInviteFocus] = useState<ShareTarget>("people");

  const router = useIonRouter();
  const { storage, isStorageReady } = useStorage();

  // Simplified format function - only handles display name and initials
  const formatFollowerRelation = useCallback(
    (item: DataSharingFollower): DisplayDataSharingFollower => {
      // ⚠️ `profile` is optional: an item the API did not expand a person for has none. Every
      // FIELD below was already guarded with `|| ""`; the OBJECT was not, and a bare
      // `profile.first_name` here aborted the whole `.map` — silently emptying the list.
      const profile = item?.profile;
      const firstName = profile?.first_name || "";
      const lastName = profile?.last_name || "";
      const fullName = `${firstName} ${lastName}`.trim();

      // Display name priority: fullName → email → User ID
      const displayName =
        fullName || profile?.email || `User ${profile?.id ?? item?.id}`;

      // Simple initials logic
      const initials =
        firstName && lastName
          ? `${firstName[0]}${lastName[0]}`.toUpperCase()
          : profile?.email
          ? profile.email.substring(0, 2).toUpperCase()
          : "U";

      return {
        ...item,
        name: displayName,
        email: profile?.email || "",
        phone: profile?.phone || null,
        dateAdded: item.created_at,
        initials,
      };
    },
    []
  );
  // useEffect(() => {
  //   const fetchPendingOutgoing = async () => {
  //     try {
  //       const outgoingRequests = await pendingRequestsActionRequired();
  //       const formattedOutgoing = outgoingRequests.map(formatFollowerRelation);
  //       setActionRequired(formattedOutgoing);
  //     } catch (error) {
  //       console.error("Error fetching outgoing requests:", error);
  //     }
  //   };
  //   fetchPendingOutgoing();
  // }, [formatFollowerRelation]);

  // Utility functions
  const showToastMessage = useCallback((message: string): void => {
    setToastMessage(message);
    setShowToast(true);
  }, []);
  const { socket } = useSocket();

  useEffect(() => {
    if (socket) {
      socket.on("dataSharing_refresh", () => {
        loadData(true);
      });
    }
    return () => {
      if (socket) {
        socket.off("dataSharing_refresh");
      }
    };
  }, [socket]);
  const handleBack = useCallback((): void => {
    router.push("/settings", "back", "pop");
  }, [router]);

  // Load data function with error handling
  const loadData = async (forceReload = false): Promise<void> => {
    try {
      setIsLoading(true);
      let statsData;
      if (!forceReload) {
        statsData = await getIonicStorage(storage, "dataSharingStats");
        setStats({
          following_count: statsData?.following_count,
          followers_count: statsData?.followers_count,
          pending_outgoing: {
            follow_requests: actionRequired.length,
            total: actionRequired.length,
          },
        });
        // Try to get cached data first for better UX
        const [cachedFollowers, cachedPendingRequests] = await Promise.all([
          getStoredPreferences("followers") as Promise<
            DataSharingFollower[] | null
          >,
          getStoredPreferences("pendingFollowerRequests") as Promise<
            DataSharingFollower[] | null
          >,
        ]);

        const actionRequiredData = await getIonicStorage(
          storage,
          "action-required"
        );
        if (actionRequiredData) {
          setActionRequired(actionRequiredData || []);
        }
        // Show cached data immediately if available
        if (
          cachedFollowers?.length ||
          cachedPendingRequests?.length ||
          actionRequiredData?.length > 0 ||
          statsData?.following_count > 0 ||
          statsData?.followers_count > 0
        ) {
          setFollowers((cachedFollowers || []).map(formatFollowerRelation));
          setPendingFollowerRequests(
            (cachedPendingRequests || []).map(formatFollowerRelation)
          );
          setIsLoading(false);
        }
      }
      const pending = await pendingRequestsActionRequired();
      const formattedOutgoing = pending.map(formatFollowerRelation);
      setActionRequired(formattedOutgoing);
      if (formattedOutgoing.length > 0) {
        await ionicStorage(storage, "action-required", formattedOutgoing);
      }
      // Fetch fresh data from API - simplified response handling
      const [freshFollowers, freshPendingRequests] = await Promise.all([
        getMyFollowers(),
        getFollowerRequests({ request_type: "following" }),
      ]);
      // Update state with fresh data
      const formattedFollowers = freshFollowers.map(formatFollowerRelation);
      const formattedPendingRequests = freshPendingRequests.map(
        formatFollowerRelation
      );

      setFollowers(formattedFollowers);
      setPendingFollowerRequests(formattedPendingRequests);

      // Cache fresh data
      await Promise.all([
        storePrefrences("followers", freshFollowers),
        storePrefrences("pendingFollowerRequests", freshPendingRequests),
      ]);

      // Update stats using API
      statsData = await getStats();
    } catch (error) {
      console.error("Error loading follower data:", error);
      showToastMessage("Failed to load data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  // useEffect(() => {
  //   const fetchPendingOutgoing = async () => {
  //     try {
  //       const outgoingRequests = await pendingRequestsActionRequired();
  //       const formattedOutgoing = outgoingRequests.map(formatFollowerRelation);
  //       setActionRequired(formattedOutgoing);
  //     } catch (error) {
  //       console.error("Error fetching outgoing requests:", error);
  //     }
  //   };
  //   fetchPendingOutgoing();
  // }, [formatFollowerRelation]);

  // Initialize data on mount
  useEffect(() => {
    loadData();
  }, []);
  const getStats = async () => {
    const statsData = await relationStats();
    setStats({
      following_count: statsData?.following_count || 0,
      followers_count: statsData?.followers_count || 0,
      pending_outgoing: {
        follow_requests: actionRequired.length,
        total: actionRequired.length,
      },
    });
    await ionicStorage(storage, "dataSharingStats", statsData);
    return statsData;
  };

  const handleTabChange = useCallback(
    (tab: "followers" | "following"): void => {
      if (tab === "following") {
        router.push("/settings/following", "forward", "push");
      } else {
        setActiveTab(tab);
      }
    },
    [router]
  );
  const handleCancelRequest = async (
    follower: DisplayDataSharingFollower
  ): Promise<void> => {
    if (!follower) return;

    try {
      setPendingFollowerRequests(
        pendingFollowerRequests.filter((f) => f.id !== follower.id)
      );
      // API call using relationship_id
      await cancelFollowRequest({
        relationship_id: follower.id,
        invitation_method: follower?.invitation_method,
      });

      showToastMessage("Request cancelled successfully");
      setShowCancelModal(false);
      setSelectedFollower(null);

      // Refresh data
      await loadData(true);
    } catch (error) {
      console.error("Error cancelling request:", error);
      showToastMessage("Failed to cancel request. Please try again.");
      // Revert optimistic update on error
      await loadData();
    }
  };
  const handleRemoveFollower = useCallback(
    async (follower: DisplayDataSharingFollower): Promise<void> => {
      if (!follower?.profile?.id) return;

      try {
        // Optimistic update
        setFollowers((prev) => prev.filter((f) => f.id !== follower.id));
        setShowRevokeModal(false);
        setSelectedFollower(null);

        await removeFollower({ follower_profile_id: follower.profile.id });
        showToastMessage("Access revoked successfully");

        // Refresh data to ensure consistency
        await loadData(true);
      } catch (error) {
        console.error("Error removing follower:", error);
        showToastMessage("Failed to revoke access. Please try again.");
        await loadData(); // Revert optimistic update
      }
    },
    [loadData, showToastMessage]
  );

  const handleAcceptRequest = useCallback(
    async (follower: DisplayDataSharingFollower): Promise<void> => {
      if (!follower?.id) return;

      try {
        // Optimistic update
        setPendingFollowerRequests((prev) =>
          prev.filter((f) => f.id !== follower.id)
        );
        setSelectedFollower(null);

        await acceptFollowRequest({ relationship_id: follower.id });
        showToastMessage("Request accepted successfully");

        await loadData(true);
      } catch (error) {
        console.error("Error accepting request:", error);
        showToastMessage("Failed to accept request. Please try again.");
        await loadData();
      }
    },
    [loadData, showToastMessage]
  );

  const handleRejectRequest = useCallback(
    async (follower: DisplayDataSharingFollower): Promise<void> => {
      if (!follower?.id) return;

      try {
        // Optimistic update
        setPendingFollowerRequests((prev) =>
          prev.filter((f) => f.id !== follower.id)
        );
        setShowAcceptRejectModal(false);
        setSelectedFollower(null);

        await rejectFollowRequest({ relationship_id: follower.id });
        showToastMessage("Request rejected successfully");

        await loadData(true);
      } catch (error) {
        console.error("Error rejecting request:", error);
        showToastMessage("Failed to reject request. Please try again.");
        await loadData();
      }
    },
    [loadData, showToastMessage]
  );

  // Modal handlers
  const openRevokeModal = useCallback(
    (follower: DisplayDataSharingFollower): void => {
      setSelectedFollower(follower);
      setShowRevokeModal(true);
    },
    []
  );
  const openCancelRequestModal = useCallback(
    (follower: DisplayDataSharingFollower): void => {
      setSelectedFollower(follower);
      setShowCancelModal(true);
    },
    []
  );

  const openAcceptRejectModal = useCallback(
    (follower: DisplayDataSharingFollower): void => {
      setSelectedFollower(follower);
      setShowAcceptRejectModal(true);
    },
    []
  );

  const closeModals = useCallback(() => {
    setShowRevokeModal(false);
    setShowAcceptRejectModal(false);
    setSelectedFollower(null);
  }, []);

  const handleInviteModalClose = useCallback(async () => {
    setShowInviteModal(false);
    await loadData();
  }, [loadData]);

  return (
    <OrgSharingProvider value={orgSharing}>
    <IonPage>
      <Header
        showGreeting={false}
        showPageTitle={true}
        showUserIcon={false}
        pageTitle="Data Sharing"
        showBackButton={true}
        onBackButtonClick={handleBack}
        allPendingRequests={actionRequired}
        pageType="followers"
        onOpenPendingRequestsModal={() => {
          setPendingFollowerRequests(pendingFollowerRequests);
        }}
      />
      {/* F-469: ONE scroll container for the whole screen. The people list used to scroll inside
          its own capped box while the organizations section sat below it in a second capped
          box, so the "Share my data" content slid behind the organizations. Now the tabs stay
          pinned, and the people region and the organizations region each take their own space,
          one after the other, in a single scroll. */}
      <IonContent style={{ "--background": "#F7F5FA" }}>
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            background: "#F7F5FA",
            padding: "12px 16px 0",
          }}
        >
          <TabNavigation
            activeTab={activeTab}
            onTabChange={handleTabChange}
            followersCount={followers.length}
            followingCount={stats.following_count}
            pendingFollowerRequestsCount={pendingFollowerRequests.length}
            pendingOutgoingCount={0}
          />
        </div>

        <div
          className="w-full max-w-6xl mx-auto"
          style={{ padding: "8px 16px 0", display: "flex", flexDirection: "column" }}
        >
          {activeTab === "followers" && (
            <>
              <section aria-label="People" style={{ flexShrink: 0 }}>
              <FollowersList
                followers={followers}
                pendingIncoming={[]}
                actionRequired={actionRequired}
                pendingFollowerRequests={pendingFollowerRequests}
                onRemoveFollower={openRevokeModal}
                onCancelRequest={openCancelRequestModal}
                onAcceptRequest={handleAcceptRequest}
                onRejectRequest={openAcceptRejectModal}
                onInviteClick={() => setShowShareChooser(true)}
                isLoading={isLoading}
                connectedOrgs={leavableOrgs(orgSharing.connected, orgRelationship.association)}
                pendingOrgs={orgSharing.pending}
                onRemoveOrg={orgRemoval.request}
                orgNameById={orgSharing.nameById}
                orgBusyId={orgSharing.busyOrgId}
              />
              </section>
              <section
                aria-labelledby="data-sharing-orgs-title"
                style={{ flexShrink: 0, marginTop: 16 }}
              >
                <ShareOrgsSection rel={orgRelationship} />
              </section>
            </>
          )}
          {/* Clears the floating bottom nav pill. */}
          <div className="footer-space" style={{ height: "100px", flexShrink: 0 }} />

          {/* Modals */}
          <ShareDataChooser
            isOpen={showShareChooser}
            orgBlocked={orgSharing.blocked}
            onClose={() => setShowShareChooser(false)}
            onChoose={(target) => {
              setShowShareChooser(false);
              setInviteFocus(target);
              setShowInviteModal(true);
            }}
          />
          {showInviteModal && (
            <DataSharingModal
              mode="share"
              focus={inviteFocus}
              isOpen={showInviteModal}
              onClose={handleInviteModalClose}
              // F-515: shown by this page's own toast (still mounted after the popup closes),
              // not by the modal's own — see SharingModal.tsx's onSuccess doc comment.
              onSuccess={() => showToastMessage("Invite(s) sent successfully!")}
            />
          )}

          <OrgRemoveConfirm
            org={orgRemoval.target}
            displayName={
              orgRemoval.target ? orgDisplayName(orgRemoval.target, orgSharing.nameById) : ""
            }
            onConfirm={orgRemoval.confirm}
            onCancel={orgRemoval.cancel}
          />

          {selectedFollower && showRevokeModal && (
            <ActionButtons
              isOpen={showRevokeModal}
              onClose={closeModals}
              title="Revoke Access"
              message={`${selectedFollower.name} will no longer have access to view your data.`}
              actionButtons={[
                {
                  text: "Revoke",
                  action: () => handleRemoveFollower(selectedFollower),
                  isPrimary: true,
                },
                {
                  text: "Cancel",
                  action: closeModals,
                },
              ]}
            />
          )}

          {selectedFollower && showCancelModal && (
            <ActionButtons
              isOpen={showCancelModal}
              onClose={() => {
                setShowCancelModal(false);
                setSelectedFollower(null);
              }}
              title={
                selectedFollower.type === "outgoing_follow_request"
                  ? "Cancel Follow Request"
                  : selectedFollower.type === "outgoing_follower_request"
                  ? "Cancel Follower Request"
                  : "Cancel Request"
              }
              message={
                selectedFollower.type === "outgoing_follow_request"
                  ? `Are you sure you want to cancel your follow request to ${selectedFollower.name}?`
                  : selectedFollower.type === "outgoing_follower_request"
                  ? `Are you sure you want to cancel your request for ${selectedFollower.name} to follow you?`
                  : `Are you sure you want to cancel your request to ${selectedFollower.name}?`
              }
              actionButtons={[
                {
                  text: "Cancel Request",
                  action: () => handleCancelRequest(selectedFollower),
                  isPrimary: true,
                },
                {
                  text: "Keep Request",
                  action: () => {
                    setShowCancelModal(false);
                    setSelectedFollower(null);
                  },
                },
              ]}
            />
          )}

          {selectedFollower && showAcceptRejectModal && (
            <ActionButtons
              isOpen={showAcceptRejectModal}
              onClose={closeModals}
              title="Follow Request"
              message={`${selectedFollower.name} wants to follow you and see your data.`}
              actionButtons={[
                {
                  text: "Accept",
                  action: () => handleAcceptRequest(selectedFollower),
                  isPrimary: true,
                },
                {
                  text: "Reject",
                  action: () => handleRejectRequest(selectedFollower),
                  isPrimary: false,
                },
              ]}
            />
          )}

          <IonToast
            isOpen={showToast}
            onDidDismiss={() => setShowToast(false)}
            duration={2000}
            position="bottom"
            cssClass="custom-action-toast"
            message={`
    <div class="toast-content">
      <span>${toastMessage}</span>
      <button class="toast-btn">View</button>
    </div>
  `}
          />
        </div>
      </IonContent>
    </IonPage>
    </OrgSharingProvider>
  );
};

export default FollowersPage;
